import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import Api from '../../Api/Api.json';

// Icons for nutrition metrics
const CaloriesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v20M2 12h20" />
    <path d="M12 2a10 10 0 0 1 0 20" />
    <path d="M12 2a10 10 0 0 0 0 20" />
  </svg>
);

const WaterIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v20M2 12h20" />
    <path d="M12 2a10 10 0 0 1 0 20" />
    <path d="M12 2a10 10 0 0 0 0 20" />
  </svg>
);

// Helper functions for generating random nutrition data
const generateCaloriesData = () => {
  const caloriesValue = Math.floor(Math.random() * 1000) + 500; // 500-1500 cal
  return {
    value: caloriesValue,
    unit: 'cal',
    status: caloriesValue > 1200 ? 'Good' : caloriesValue > 800 ? 'Fair' : 'Low',
    timestamp: new Date().toLocaleTimeString()
  };
};

const generateWaterData = () => {
  const waterValue = Math.floor(Math.random() * 4) + 1; // 1-4 liters
  return {
    value: waterValue,
    unit: 'L',
    status: waterValue > 2.5 ? 'Good' : waterValue > 1.5 ? 'Fair' : 'Low',
    timestamp: new Date().toLocaleTimeString()
  };
};

const Nutrition = () => {
  const navigate = useNavigate();
  const [photoImage, setPhotoImage] = useState(null);
  const [photoBase64, setPhotoBase64] = useState(null);
  const [foodResults, setFoodResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleCardClick = (metricType) => {
    // Navigate to nutrition summary for Nutrition card
    if (metricType === 'calories') {
      navigate('/health-categories/nutrition/nutrition-summary');
    } else {
      console.log(`Clicked on ${metricType} metric`);
    }
  };

  const takeMealPhoto = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // Check camera permission first
        const permissionResult = await Camera.checkPermissions();
        
        if (permissionResult.camera === 'denied' || permissionResult.camera === 'prompt') {
          // Request camera permission
          const requestResult = await Camera.requestPermissions({ permissions: ['camera'] });
          
          if (requestResult.camera === 'denied') {
            alert('Camera permission is required to take meal photos. Please enable it in Settings.');
            return;
          }
        }
        
        // Native platform: Use camera
        const photo = await Camera.getPhoto({
          quality: 60,
          width: 1024,
          height: 1024,
          source: CameraSource.Camera,
          resultType: CameraResultType.Base64
        });

        if (photo.base64String) {
          const imageUrl = `data:image/jpeg;base64,${photo.base64String}`;
          setPhotoImage(imageUrl);
          setPhotoBase64(photo.base64String);
          setFoodResults(null);
          setErrorMessage(null);
          
          // Automatically call API after photo is taken
          await analyzeFoodPhoto(photo.base64String);
        }
      } else {
        // Web platform: Use file input
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
          const file = e.target.files[0];
          if (!file) return;

          try {
            // Convert file to base64
            const base64String = await fileToBase64(file);
            const imageUrl = `data:image/jpeg;base64,${base64String}`;
            setPhotoImage(imageUrl);
            setPhotoBase64(base64String);
            setFoodResults(null);
            setErrorMessage(null);
            
            // Automatically call API after photo is selected
            await analyzeFoodPhoto(base64String);
          } catch (error) {
            console.error('File conversion error:', error);
            alert('Failed to process the image. Please try again.');
          }
        };
        input.click();
      }
    } catch (error) {
      console.error('Camera Error:', error);
      if (error.message !== 'User cancelled photos app') {
        alert('Failed to take photo. Please try again.');
      }
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const analyzeFoodPhoto = async (base64String) => {
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      // Get user's timezone (e.g., 'Asia/Dhaka')
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Get user_id from localStorage
      const userInfo = localStorage.getItem('user_info') 
        ? JSON.parse(localStorage.getItem('user_info'))
        : null;
      const userId = userInfo?.di_id || null;
      
      const headers = { headers: { "Content-Type": "application/json" } };
      const url = Api.FITNESS_API;
      const requestData = {
        task: "getFoodCalories",
        photo_str: base64String,
        language: "english",
        timezone: timezone,
        user_id: userId
      };
      console.log(requestData);
      const response = await axios.post(url, requestData, headers);
      
      // Handle different response structures
      let responseData = response.data;
      if (responseData?.body) responseData = responseData.body;
      if (responseData?.data) responseData = responseData.data;
      console.log(responseData);
      if (responseData?.status === "success" && responseData?.results) {
        setFoodResults(responseData.results);
        setShowResultsModal(true);
      } else if (responseData?.status === "failure") {
        setErrorMessage("Did not find food item in the photo. Please try again with a clearer image.");
        setShowResultsModal(true);
      } else {
        setErrorMessage("Unable to analyze the photo. Please try again.");
        setShowResultsModal(true);
      }
    } catch (error) {
      console.error('API Error:', error);
      setErrorMessage("Failed to analyze photo. Please try again.");
      setShowResultsModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const closeResultsModal = () => {
    setShowResultsModal(false);
    setFoodResults(null);
    setErrorMessage(null);
  };

  const nutritionMetrics = [
    {
      id: 'calories',
      name: 'Nutrition',
      bengaliName: 'Nutrition',
      icon: <CaloriesIcon />,
      description: 'Track your daily calorie intake and energy consumption',
      color: 'bg-[#58A6FF]',
      unit: 'cal',
      normalRange: '1500-2500',
      currentValue: generateCaloriesData().value,
      status: generateCaloriesData().status
    },
    {
      id: 'water',
      name: 'Water Intake',
      bengaliName: 'Water Intake',
      icon: <WaterIcon />,
      description: 'Monitor your daily water consumption and hydration levels',
      color: 'bg-[#58A6FF]',
      unit: 'L',
      normalRange: '2-4',
      currentValue: generateWaterData().value,
      status: generateWaterData().status
    }
  ];

  return (
    <div className="min-h-screen pb-20 safe-area-top">
      {/* Black background */}
      <div className="absolute inset-0 bg-black"></div>
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white hover:text-blue-400 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium text-white">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Nutrition & Hydration</h1>
          <p className="text-white">Track your daily nutrition and hydration intake</p>
        </div>



        {/* Nutrition Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {nutritionMetrics.map((metric, index) => (
            <div
              key={metric.id}
              onClick={() => handleCardClick(metric.id)}
              className={`bg-[#161B22] rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer overflow-hidden group border border-[#30363D] ${
                index === nutritionMetrics.length - 1 ? 'mb-20' : ''
              }`}
            >
              {/* Metric Header */}
              <div className={`${metric.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      {metric.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{metric.name}</h3>
                      <p className="text-sm opacity-90">{metric.bengaliName}</p>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>

              {/* Metric Content */}
              <div className="p-6">
                <p className="text-white text-sm leading-relaxed mb-4">
                  {metric.description}
                </p>

                {/* Current Value */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-3xl font-bold text-white">{metric.currentValue}</span>
                    <span className="text-white ml-1">{metric.unit}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white">Status</div>
                    <div className={`text-xs font-medium ${
                      metric.status === 'Good' ? 'text-green-400' :
                      metric.status === 'Fair' ? 'text-yellow-400' :
                      'text-red-400'
                    }`}>
                      {metric.status}
                    </div>
                  </div>
                </div>

                {/* Normal Range */}
                <div className="bg-[#21262D] rounded-lg p-3 mb-4 border border-[#30363D]">
                  <div className="text-sm text-white mb-1">Daily Goal</div>
                  <div className="font-medium text-white">{metric.normalRange} {metric.unit}</div>
                </div>

                {/* Take Meal Photo Button - Only for Nutrition card */}
                {metric.id === 'calories' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      takeMealPhoto();
                    }}
                    disabled={isLoading}
                    className="w-full py-2 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Analyzing...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{Capacitor.isNativePlatform() ? 'Take Meal Photo' : 'Upload Meal Photo'}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Food Analysis Results Modal */}
        {showResultsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#161B22] rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-[#30363D]">
              {/* Modal Header */}
              <div className="sticky top-0 bg-[#161B22] border-b border-[#30363D] px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-white">Food Analysis Results</h2>
                <button
                  onClick={closeResultsModal}
                  className="text-white hover:text-gray-400 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {errorMessage ? (
                  <div className="text-center py-8">
                    <div className="text-red-400 text-lg font-medium mb-2">{errorMessage}</div>
                    {photoImage && (
                      <img
                        src={photoImage}
                        alt="Captured meal"
                        className="max-w-xs mx-auto rounded-lg shadow-md mt-4"
                      />
                    )}
                  </div>
                ) : foodResults && foodResults.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Image on Left */}
                    <div className="flex items-center justify-center">
                      {photoImage && (
                        <img
                          src={photoImage}
                          alt="Captured meal"
                          className="max-w-full h-auto rounded-lg shadow-md"
                        />
                      )}
                    </div>

                    {/* Table on Right */}
                    <div className="flex flex-col">
                      <h3 className="text-xl font-semibold text-white mb-4">Detected Food Items</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-[#21262D] border border-[#30363D] rounded-lg">
                          <thead className="bg-[#58A6FF] text-white">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-semibold">Item</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold">Calories</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-[#30363D]">
                            {foodResults.map((item, index) => {
                              // Extract item name, amount, and calories from the result object
                              // Try different possible keys for item name
                              const itemKey = `item_${index + 1}`;
                              let itemName = item[itemKey] || item.item || item[`item_${index}`] || 'Unknown';
                              
                              // If still not found, try to find any key starting with "item_"
                              if (itemName === 'Unknown') {
                                const itemKeys = Object.keys(item).filter(key => key.startsWith('item_'));
                                if (itemKeys.length > 0) {
                                  itemName = item[itemKeys[0]];
                                }
                              }
                              
                              const amount = item.amount || 'N/A';
                              const calories = item.calories || 'N/A';
                              
                              return (
                                <tr key={index} className="hover:bg-[#1f2937]">
                                  <td className="px-4 py-3 text-sm text-white font-medium">{itemName}</td>
                                  <td className="px-4 py-3 text-sm text-white">{amount}</td>
                                  <td className="px-4 py-3 text-sm text-white font-semibold">{calories} cal</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-[#21262D]">
                            <tr>
                              <td className="px-4 py-3 text-sm font-bold text-white" colSpan="2">Total Calories</td>
                              <td className="px-4 py-3 text-sm font-bold text-green-400">
                                {foodResults.reduce((sum, item) => {
                                  const calories = parseInt(item.calories) || 0;
                                  return sum + calories;
                                }, 0)} cal
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-white">No results to display</div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-[#161B22] border-t border-[#30363D] px-6 py-4 flex justify-end">
                <button
                  onClick={closeResultsModal}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default Nutrition;
